import { AppointmentWindow, usePortalMode } from '@servicetitan/cp-ui';
import {
    Avatar,
    BodyText,
    Card,
    Divider,
    Headline,
    Stack,
    Tag,
    TagPropsStrict,
} from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';
import { tokens } from '@servicetitan/tokens/core';
import { observer } from 'mobx-react';
import { FC, Fragment } from 'react';
import { JobAppointmentItem, JobAppointmentItemStatus } from '../../../../../api/portal-jobs.api';
import { SettingsStore } from '../../../../../api/settings.store';
import { getAppointmentState } from '../../../../../utils/helpers';

interface JobAppointmentsItemProps {
    className?: string;
    appointment: JobAppointmentItem;
    isDrawer: boolean;
}

export const JobAppointmentsItem: FC<JobAppointmentsItemProps> = observer(props => {
    const { appointment, className, isDrawer } = props;
    const mode = usePortalMode();
    const [{ appointmentTimeOptions }] = useDependencies(SettingsStore);

    const technicians = appointment.technicians;
    const showTechnicians =
        appointment.status !== JobAppointmentItemStatus.Canceled &&
        !!technicians?.length &&
        technicians?.length > 0;
    const leftStyles = {
        borderLeft: `none`,
        borderTopLeftRadius: `0`,
        borderBottomLeftRadius: `0`,
    };
    const techniciansListBody = (
        <Stack spacing={2} alignItems="center">
            {technicians?.map(technician => (
                <BodyText bold={isDrawer} key={technician.id} size="small">
                    <Avatar icon="person" size="s" className="m-r-1 m-b-half" color="#EAEFF2" />
                    {technician.name}
                </BodyText>
            ))}
        </Stack>
    );
    const techniciansList = isDrawer ? (
        <Card.Section style={leftStyles}>{techniciansListBody}</Card.Section>
    ) : (
        <Fragment>
            <Divider vertical spacing="2" style={{ height: '50%' }} />
            {techniciansListBody}
        </Fragment>
    );

    const statusBorderColor = AppointmentStatusBorderColor[appointment.status];

    return (
        <Card
            padding="thin"
            className={className}
            style={{ borderLeft: `6px solid ${statusBorderColor}` }}
        >
            <Card.Section style={leftStyles} light>
                <Stack alignItems="center" spacing={1}>
                    <Headline size={isDrawer ? 'small' : 'medium'} className="m-b-0 t-truncate">
                        {'Appointment #' + appointment.number}
                    </Headline>
                    <AppointmentStatusTag status={appointment.status} />
                </Stack>
            </Card.Section>
            <Card.Section style={leftStyles}>
                <Stack
                    spacing={3}
                    alignItems="center"
                    wrap={mode.desktop ? 'nowrap' : 'wrap'}
                    className="m-b-1"
                >
                    {appointmentTimeOptions && (
                        <AppointmentWindow
                            appointmentState={getAppointmentState(appointment.status)}
                            appointmentTimeOptions={appointmentTimeOptions}
                            startDate={appointment.startDate}
                            endDate={appointment.endDate}
                            arrivalWindowPlanStartDate={appointment.arrivalWindowPlanStartDate}
                            arrivalWindowPlanEndDate={appointment.arrivalWindowPlanEndDate}
                            size="small"
                            bold={isDrawer}
                        />
                    )}
                    {!isDrawer && showTechnicians && techniciansList}
                </Stack>
            </Card.Section>
            {isDrawer && showTechnicians && techniciansList}
        </Card>
    );
});

const AppointmentStatusBorderColor: Record<JobAppointmentItemStatus, string> = {
    [JobAppointmentItemStatus.Scheduled]: tokens.colorPurple400,
    [JobAppointmentItemStatus.Dispatched]: tokens.colorGreen400,
    [JobAppointmentItemStatus.Working]: tokens.colorGreen400,
    [JobAppointmentItemStatus.OnHold]: tokens.colorYellow400,
    [JobAppointmentItemStatus.Completed]: tokens.colorGreen400,
    [JobAppointmentItemStatus.Canceled]: tokens.colorRed400,
};

interface AppointmentStatusTagProps {
    status: JobAppointmentItemStatus;
}

const AppointmentStatusTag: FC<AppointmentStatusTagProps> = props => {
    const { status } = props;
    const colorData = AppointmentStatusColor[status];
    return (
        <Tag color={colorData[0]} subtle={colorData[1]}>
            {AppointmentStatusText[status]}
        </Tag>
    );
};

const AppointmentStatusColor: Record<JobAppointmentItemStatus, [TagPropsStrict['color'], boolean]> =
    {
        [JobAppointmentItemStatus.Scheduled]: ['info', true],
        [JobAppointmentItemStatus.Dispatched]: [tokens.colorGreen400, false],
        [JobAppointmentItemStatus.Working]: [tokens.colorGreen400, false],
        [JobAppointmentItemStatus.OnHold]: ['warning', true],
        [JobAppointmentItemStatus.Completed]: ['success', true],
        [JobAppointmentItemStatus.Canceled]: ['critical', true],
    };

const AppointmentStatusText: Record<JobAppointmentItemStatus, string> = {
    [JobAppointmentItemStatus.Scheduled]: 'Scheduled',
    [JobAppointmentItemStatus.Dispatched]: 'In Progress',
    [JobAppointmentItemStatus.Working]: 'In Progress',
    [JobAppointmentItemStatus.OnHold]: 'On Hold',
    [JobAppointmentItemStatus.Completed]: 'Completed',
    [JobAppointmentItemStatus.Canceled]: 'Canceled',
};
